import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, FirstDataRenderedEvent, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import isEqual from 'lodash/isEqual';
import { firstValueFrom } from 'rxjs';

import { injectListIdOrganizationsQuery } from '@models/organization/list-id-organizations-query';
import { injectPurchaseOrderQuery } from '@models/purchase-order/purchase-order-list-query';
import { injectPivotSettings } from '@models/trial-attributes/trial-attributes';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { syncGridOptions } from '@shared/utils/ag-sync-grid-options.util';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { normalizeAndFill } from '@shared/utils/normalize-and-fill.util';
import { Utils } from '@shared/utils/utils';

import {
  buildBalanceSheetRows,
  getPendingBalanceSheetChanges,
} from './grid-config/balance-sheet-row-data.util';
import { manageBalanceSheetsAccountsGridConfig } from './grid-config/grid-config';
import { BalanceSheetAccount, BalanceSheetEntitySettings } from './grid-config/grid-config-context';
import { injectGetBalanceSheetsQuery } from '../../models/get-balance-sheets-query';
import { PendingGridChanges } from '../../new-manage-balance-sheets-accounts-component-modal.component';
import { useBalanceSheetAttributesContext } from '../../services/balance-sheet-attributes.context';
import { useBalanceSheetHeaderContext } from '../../services/balance-sheet-header.context';
import { useBalanceSheetPoAttributeContext } from '../../services/balance-sheet-po-attribute.context';
import { useBalanceSheetProgramProjectContext } from '../../services/balance-sheet-program-project.context';

const MAX_COLUMNS_LENGTH = 13; // PO # + Custom Attributes

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    TableSkeletonComponent,
    FormsModule,
    NgSelectModule,
    TablePanelComponent,
    ButtonComponent,
    CheckboxComponent,
    TooltipDirective,
  ],
  selector: 'aux-new-manage-balance-sheets-accounts',
  templateUrl: './new-manage-balance-sheets-accounts.component.html',
})
export class NewManageBalanceSheetsAccountsComponent {
  overlayService = inject(OverlayService);

  flags = injectFlags({
    journalEntryApManualClearing: 'journal_entry_ap_manual_clearing',
  });
  listIdOrganizationsQuery = injectListIdOrganizationsQuery();

  vendors = computed(() => {
    const data = this.listIdOrganizationsQuery.data();

    if (!data) return [];

    return data
      .filter((org) => org.organization_type === 'ORGANIZATION_VENDOR')
      .sort((a, b) => Utils.localeAlphaNumSort(a.name, b.name));
  });

  selectedVendor = linkedSignal(() => {
    const vendors = this.vendors();

    return vendors[0]?.id ?? '';
  });

  getBalanceSheetsQuery = injectGetBalanceSheetsQuery({
    vendorId: this.selectedVendor.asReadonly(),
  });
  pivotSettings = injectPivotSettings();
  purchaseOrderQuery = injectPurchaseOrderQuery();

  gridAPI = signal<GridApi | null>(null);
  isApplyToAllVendors = signal(false);

  originalRowData = linkedSignal(() => {
    this.selectedVendor();

    const flags = this.flags();
    const data = this.getBalanceSheetsQuery.data();
    const jeApManualClearing = flags.journalEntryApManualClearing;

    try {
      const parsedSettings: BalanceSheetEntitySettings = JSON.parse(data?.[0]?.settings || '{}');

      return buildBalanceSheetRows(parsedSettings, jeApManualClearing);
    } catch {
      return buildBalanceSheetRows({} as BalanceSheetEntitySettings, jeApManualClearing);
    }
  });

  attributesContext = useBalanceSheetAttributesContext({
    originalRowData: this.originalRowData.asReadonly(),
  });

  rowData = linkedSignal(() => {
    return JSON.parse(JSON.stringify(this.originalRowData())) as BalanceSheetAccount[];
  });

  onAttributeDeleteEffect = (attributeName: string) => {
    const encodedKeyToRemove = getEncodedAttributeName(attributeName);
    const updatedRows = this.rowData().map((row) => {
      const { [encodedKeyToRemove]: _, ...restOfAttributes } = row.attributes;

      return {
        ...row,
        attributes: restOfAttributes,
      };
    });

    this.rowData.set(updatedRows);

    this.gridAPI()!.sizeColumnsToFit();
  };

  headerContext = useBalanceSheetHeaderContext({
    attributesContext: this.attributesContext,
    onAttributeDeleteEffect: this.onAttributeDeleteEffect,
  });

  addNewAttributeDisabled = computed(() => {
    // Track headerState to recompute when headers change
    this.headerContext.headerState();

    const gridAPI = this.gridAPI();

    if (!gridAPI) return true;

    // Get all columns from the grid
    const columnDefs = gridAPI.getColumnDefs();

    if (!columnDefs) return true;

    // Count only attribute columns (those that start with 'attributes.')
    const attributeColumns = columnDefs.filter((col) =>
      (col as ColDef).field?.startsWith('attributes.')
    );
    const currentAttributesCount = attributeColumns.length;

    return currentAttributesCount >= MAX_COLUMNS_LENGTH;
  });

  poAttributeContext = useBalanceSheetPoAttributeContext({
    vendorId: this.selectedVendor.asReadonly(),
  });
  programProjectContext = useBalanceSheetProgramProjectContext({
    originalRowData: this.originalRowData.asReadonly(),
  });

  gridOptions = computed(() => {
    const flags = this.flags();
    const pivotSettings = this.pivotSettings.data() || [];

    this.selectedVendor();

    // Pass attributesContext, poAttributeContext, and programProjectContext to grid config
    return untracked(() => {
      const { headerState, onAttributeDelete, onAttributeRename } = this.headerContext;

      return {
        ...manageBalanceSheetsAccountsGridConfig({
          attributesContext: this.attributesContext,
          flags,
          headerState,
          onAttributeDelete: (attributeName: string) =>
            onAttributeDelete(attributeName, this.gridAPI()!),
          onAttributeRename: (attributeName: string, newName: string) =>
            onAttributeRename(attributeName, newName, this.gridAPI()!),
          pivotSettings,
          poAttributeContext: this.poAttributeContext,
          programProjectContext: this.programProjectContext,
        }),
        onCellValueChanged: () => {
          // to trigger dirty state
          this.rowData.set([...this.rowData()]);
        },
      };
    });
  });
  // Computed signal to check if the grid is dirty (has unsaved changes)
  isDirty = computed(() => {
    const normalizedRowData = normalizeAndFill(this.rowData(), this.originalRowData());
    const normalizedOriginalRowData = normalizeAndFill(this.originalRowData(), this.rowData());
    const isRowDataDirty = !isEqual(normalizedRowData, normalizedOriginalRowData);
    const isHeaderDirty = this.headerContext.hasUnsavedChanges();

    return isRowDataDirty || isHeaderDirty;
  });
  loading = computed(
    () =>
      this.listIdOrganizationsQuery.isLoading() ||
      this.getBalanceSheetsQuery.isLoading() ||
      this.pivotSettings.loading()
  );

  selectDropdown = viewChild<NgSelectComponent>(NgSelectComponent);

  // Call this after saving to reset the dirty state
  markClean = () => {
    this.headerContext.onClearState();
    this.rowData.set(JSON.parse(JSON.stringify(this.originalRowData())));
  };

  // This will reset the grid options to the initial state whenever the dependent signals change
  _gridOptionsSync = effect(() => {
    const gridAPI = untracked(() => this.gridAPI()!);
    const gridOptions = this.gridOptions();

    if (!gridAPI || !gridOptions) {
      return;
    }

    syncGridOptions(gridAPI, gridOptions);
    // so after this effect runs, we need to clear every state here
    this.markClean();
  });

  getPendingChanges = (): PendingGridChanges => {
    const headerStateSnapshot = this.headerContext.headerState();
    const currentRowData = this.rowData() as BalanceSheetAccount[];
    const currentColumns = this.gridAPI()!
      .getColumnDefs()!
      .filter((col) => (col as ColDef)!.field!.startsWith('attributes.'))
      .map((col) => (col as ColDef).colId as string);

    return getPendingBalanceSheetChanges({
      currentColumns,
      currentRowData,
      headerStateSnapshot,
    });
  };

  onAddNewAttribute = () => {
    this.headerContext.onAddNewAttribute(this.gridAPI()!);
  };

  onFirstDataRendered = (event: FirstDataRenderedEvent) => {
    const gridAPI = event.api;

    if (!gridAPI) return;

    gridAPI.sizeColumnsToFit();
  };

  onGridReady = (params: GridReadyEvent) => {
    this.gridAPI.set(params.api);
  };

  onVendorChangeAttempt = (event: MouseEvent, newVendorId: string) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.selectDropdown()) {
      this.selectDropdown()?.close();
    }

    if (this.isDirty()) {
      // need to use setTimeout because when we are using auxTooltipPanelContainerClass for tooltip zIndex in modal it breaks confirm modal
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      setTimeout(async () => {
        const ref = this.overlayService.openUnsavedChangesConfirmation();
        const result = await firstValueFrom(ref.afterClosed$);

        if (result?.data) {
          this.selectedVendor.set(newVendorId);
        }
      }, 0);

      // If not accepted, do nothing (keep the old value)
    } else {
      this.selectedVendor.set(newVendorId);
    }
  };
}
